import { Injectable } from '@angular/core';
import { Action, select, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  finalize,
  map,
  mergeMap,
  retry,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import {
  loadConfiguracionDePago,
  loadPago,
  registerPago,
  registerPagoFailed,
  registerPagoSuccess,
  savePago,
  saveConfiguracionDePago,
  registerOtroMedioDePago,
  registerOtroMedioDePagoSuccess,
  registerOtroMedioDePagoFailed,
  loadConfiguracionDePagoFailed,
  consultarEstadoPagoFailed,
} from 'src/app/store/actions/pago.action';
import { ConfiguracionDePago } from 'src/app/pago/models/ConfiguracionDePago.model';
import { AppState } from 'src/app/store/state/app.state';
import {
  selectConfiguracionDePago,
  selectIdPago,
  selectProducto,
} from 'src/app/store/selectors/pago.select';
import { PagoFactory } from 'src/app/pago/factories/pago/pago.factory';
import {
  Log,
  LogService,
  SuraLoadingService,
} from '@segurosvoluntarios_vel/commons-services';
import { LoadingTag } from 'src/app/pago/enums/LoadingTag.enum';
import { Router } from '@angular/router';
import { Ruta } from 'src/app/pago/enums/Ruta.enum';
import { TenantManagerService } from 'src/app/pago/services/tenan-manager/tenant-manager.service';
import { Pago } from 'src/app/pago/models/Pago.model';
import { ErrorPago } from 'src/app/pago/models/ErrorPago.model';
import { HttpErrorResponse } from '@angular/common/http';
import { PagoService } from 'src/app/pago/services/pago/pago.service';

@Injectable()
export class PagoEffect {
  tenant: string;
  producto: string;
  productoTemp: string;

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private pagoService: PagoService,
    private pagoFactory: PagoFactory,
    public suraLoadingService: SuraLoadingService,
    private tenantManagerService: TenantManagerService,
    private router: Router,
    private logservie: LogService
  ) {
    this.tenantManagerService.tenant
      .pipe(take(1))
      .subscribe((tenant: string) => {
        this.tenant = tenant;
      });
  }
  ngOnInit() {
    this.getNombreProducto();
    this.getNombreProductoTemp();
  }

  getNombreProducto() {
    this.store.pipe(select(selectProducto)).subscribe(pago => {
      this.producto = pago;
    });
  }

  getNombreProductoTemp() {
    this.store.pipe(select(selectConfiguracionDePago)).subscribe(pago => {
      this.productoTemp = pago.idProducto;
    });
  }

  getPago$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadConfiguracionDePago),
      mergeMap(action => {
        this.suraLoadingService.addLoadingTag(LoadingTag.PURCHASE);
        return this.pagoService.getPago(action.idPago).pipe(
          map((configuracionDePago: ConfiguracionDePago) => {
            return saveConfiguracionDePago({
              configuracionDePago,
            });
          }),
          catchError((response: HttpErrorResponse) => {
            const body: Log = {
              body: JSON.stringify(response.error),
              origen: 'pagos-mf',
              tenant: this.tenant,
              producto: this.producto || this.productoTemp,
              transactionId: response.error?.requestId,
            };
            this.logservie.error(body);
            const errors = this.getError(response);
            return of(loadConfiguracionDePagoFailed({ error: errors }));
          }),
          finalize(() => {
            this.suraLoadingService.removeLoadingTag(LoadingTag.PURCHASE);
          })
        );
      })
    )
  );

  registerPago$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(registerPago),
      concatMap(action =>
        of(action).pipe(withLatestFrom(this.store.pipe(select(selectIdPago))))
      ),
      mergeMap(([action, idPagoInState]) => {
        this.suraLoadingService.addLoadingTag(LoadingTag.PURCHASE);
        return this.pagoService
          .registerPago(
            idPagoInState,
            this.pagoFactory.convertToDto(action.pago)
          )
          .pipe(
            map(() => {
              this.router.navigate([
                `${this.tenant}/${Ruta.VALIDACION}/${idPagoInState}`,
              ]);
              return registerPagoSuccess();
            }),
            catchError(response => {
              const body: Log = {
                body: JSON.stringify(response.error),
                origen: 'pagos-mf',
                tenant: this.tenant,
                producto: this.producto || this.productoTemp,
                transactionId: response.error?.requestId,
              };
              this.logservie.error(body);
              const errors = this.getError(response);
              return of(registerPagoFailed({ error: errors }));
            }),
            finalize(() => {
              this.suraLoadingService.removeLoadingTag(LoadingTag.PURCHASE);
            })
          );
      })
    )
  );

  registerOtroMedioDePago$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(registerOtroMedioDePago),
      concatMap(action =>
        of(action).pipe(withLatestFrom(this.store.pipe(select(selectIdPago))))
      ),
      mergeMap(([action, idPagoInState]) => {
        this.suraLoadingService.addLoadingTag(LoadingTag.PURCHASE);
        return this.pagoService.registerOtroMedioDePago(action.pago).pipe(
          map((urlRedirectOtroMedioDePago: string) => {
            return registerOtroMedioDePagoSuccess({
              urlRedirectOtroMedioDePago,
            });
          }),
          catchError((response: HttpErrorResponse) => {
            const body: Log = {
              body: JSON.stringify(response.error),
              origen: 'pagos-mf',
              tenant: this.tenant,
              producto: this.producto || this.productoTemp,
              transactionId: response.error?.requestId,
            };
            this.logservie.error(body);
            const errors = this.getError(response);
            return of(registerOtroMedioDePagoFailed({ error: errors }));
          }),
          finalize(() => {
            this.suraLoadingService.removeLoadingTag(LoadingTag.PURCHASE);
          })
        );
      })
    )
  );

  loadPago$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPago),
      retry(3),
      delay(5000),
      mergeMap(action => {
        return this.pagoService.getEstadoPago(action.idPago).pipe(
          map((pago: Pago) => {
            return savePago({
              pago,
            });
          }),
          catchError((response: HttpErrorResponse) => {
            const body: Log = {
              body: JSON.stringify(response.error),
              origen: 'pagos-mf',
              tenant: this.tenant,
              producto: this.producto || this.productoTemp,
              transactionId: response.error?.requestId,
            };
            this.logservie.error(body);
            const errors = this.getError(response);
            return of(consultarEstadoPagoFailed({ error: errors }));
          })
        );
      })
    )
  );

  private getError(httpError: HttpErrorResponse): ErrorPago[] {
    if (Array.isArray(httpError.error)) {
      return httpError.error as ErrorPago[];
    }
    return [
      {
        codigo: httpError.error?.statusCode || httpError.statusText,
        mensaje: httpError.error?.message || httpError.message,
      },
    ];
  }
}
